// materialManager.js
import { fetchMaterials } from "./api";
import { getTranslations } from "./components/translations";
import { debug } from "./debug";

class MaterialManager {
  constructor() {
    this.configurableMaterials = []; // Materials from the 3D viewer
    this.allMaterials = []; // All materials from our database (global list)
    this.materialsByName = {}; // Materials indexed by name for quick lookup
    this.materialsBySlug = {}; // Materials indexed by slug for quick lookup
    this.familySettings = {}; // Family-specific material settings
    this.productSettings = {}; // Product-specific material settings (overrides family)
    this.initialized = false;
    this.currentFamily = null; // Added to store the current family
    this.currentProduct = null; // Added to store the current product
    this.collectionTranslations = {}; // NUOVO: per le traduzioni delle collezioni
  }

  // Initialize all materials - fetch from the server
  async initializeAllMaterials() {
    this.allMaterials = [];
    this.materialsByName = {};
    this.materialsBySlug = {};

    try {
      // Fetch materials from the server
      const materialsData = await fetchMaterials();
      const serverMaterials = materialsData.materials || [];

      // Process all materials from the server
      serverMaterials.forEach((material) => {
        // IMPORTANTE: Preserva l'ID originale E la proprietà hover
        const cleanedMaterial = {
          id: material.id, // PRESERVA L'ID ORIGINALE
          name: material.name.trim(), // Pulisci gli spazi
          slug: material.slug
            ? material.slug.trim()
            : this.generateSlug(material.name.trim()),
          thumbnail: material.thumbnail || "",
          hover: material.hover || null, // AGGIUNTO: Preserva la proprietà hover
          collection: material.collection || "Default",
          type: material.type || "Default",
          technicalTypes: material.technicalTypes || [],
        };

        // Add the material to our collections
        this.allMaterials.push(cleanedMaterial);
        this.materialsByName[cleanedMaterial.name] = cleanedMaterial;
        this.materialsBySlug[cleanedMaterial.slug] = cleanedMaterial;

        // Also index by normalized name and slug for fuzzy matching
        const normalizedName = this.normalize(cleanedMaterial.name);
        const normalizedSlug = this.normalize(cleanedMaterial.slug);
        if (!this.materialsByName[normalizedName]) {
          this.materialsByName[normalizedName] = cleanedMaterial;
        }
        if (!this.materialsBySlug[normalizedSlug]) {
          this.materialsBySlug[normalizedSlug] = cleanedMaterial;
        }

        // Process variations if they exist
        if (material.variations && Array.isArray(material.variations)) {
          material.variations.forEach((variation, index) => {
            const varMaterial = {
              id: variation.id || `${material.id}_var_${index}`, // PRESERVA O GENERA ID PER VARIATION
              name: variation.name.trim(),
              slug: variation.slug
                ? variation.slug.trim()
                : this.generateSlug(variation.name.trim()),
              thumbnail: variation.thumbnail || "",
              hover: variation.hover || null, // AGGIUNTO: Preserva la proprietà hover anche per le variazioni
              collection: material.collection || "Default",
              type: material.type || "Default",
            };

            this.allMaterials.push(varMaterial);
            this.materialsByName[varMaterial.name] = varMaterial;
            this.materialsBySlug[varMaterial.slug] = varMaterial;

            // Also index normalized values
            const normalizedVarName = this.normalize(varMaterial.name);
            const normalizedVarSlug = this.normalize(varMaterial.slug);
            if (!this.materialsByName[normalizedVarName]) {
              this.materialsByName[normalizedVarName] = varMaterial;
            }
            if (!this.materialsBySlug[normalizedVarSlug]) {
              this.materialsBySlug[normalizedVarSlug] = varMaterial;
            }
          });
        }
      });

      debug.materials(`Initialized ${this.allMaterials.length} materials from server`);
    } catch (error) {
      console.error("Failed to fetch materials from server:", error);
      debug.warn.materials("Using empty materials array as fallback");
    }

    return this.allMaterials;
  }

  // Helper function to normalize strings for matching
  normalize(str) {
    return String(str || "")
      .toLowerCase()
      .replace(/[\s_-]+/g, "")
      .trim();
  }

  // NEW: Helper function to check if a variation matches the configuration
  isVariationInConfiguration(variation, typeSettings) {
    if (!typeSettings) return true;

    const name = variation.name || "";
    const slug = variation.slug || this.generateSlug(name);
    const normalizedName = this.normalize(name);
    const normalizedSlug = this.normalize(slug);

    // Check against standard materials
    if (Array.isArray(typeSettings.standard)) {
      for (const stdMat of typeSettings.standard) {
        const normalizedStdMat = this.normalize(stdMat);
        if (
          normalizedName === normalizedStdMat ||
          normalizedSlug === normalizedStdMat ||
          name === stdMat ||
          slug === stdMat
        ) {
          return true;
        }
      }
    }

    // Check against optional materials
    if (Array.isArray(typeSettings.optional)) {
      for (const optMat of typeSettings.optional) {
        const normalizedOptMat = this.normalize(optMat);
        if (
          normalizedName === normalizedOptMat ||
          normalizedSlug === normalizedOptMat ||
          name === optMat ||
          slug === optMat
        ) {
          return true;
        }
      }
    }

    return false;
  }

  // Initialize with viewer materials and family/product settings
  initialize(viewerMaterials = [], familyName = null, productCode = null) {
    debug.materials("Initializing:", { viewerMaterialsCount: viewerMaterials.length, familyName, productCode });

    // Reset configurable materials
    this.configurableMaterials = [];

    // First, make sure we have all materials initialized
    if (this.allMaterials.length === 0) {
      this.initializeAllMaterials();
    }

    // Store current family and product for use in getAvailableMaterials
    this.currentFamily = familyName;
    this.currentProduct = productCode;

    // Process configurable materials from the viewer
    viewerMaterials.forEach((mat) => {
      // Process material variations
      const enrichedVariations = mat.materialVariations.map(
        (variation, index) => {
          // Clean the name from extra whitespace
          const cleanName = variation.name.trim();

          // Generate a slug from the name for matching
          const generatedSlug = this.generateSlug(cleanName);

          // Extract code prefix if available (e.g., "V47" from "V47 Beige red")
          const prefixMatch = cleanName.match(/^([A-Z]+\d+)\s+/);
          const codePrefix = prefixMatch ? prefixMatch[1] : null;

          // Try to find this material in our global list with multiple strategies
          let matchingMaterial = null;

          // 1. Try exact slug match
          matchingMaterial = this.findMaterialBySlug(generatedSlug);

          // 2. If not found, try code prefix match
          if (!matchingMaterial && codePrefix) {
            matchingMaterial = this.findMaterialByName(codePrefix);
          }

          // 3. If still not found, try full name match
          if (!matchingMaterial) {
            matchingMaterial = this.findMaterialByName(cleanName);
          }

          // 4. If no match, create default values with generated ID
          if (!matchingMaterial) {
            // For materials with codes like "V47", use only that as the display name
            const displayName = codePrefix || cleanName;

            matchingMaterial = {
              id: `generated_${mat.name}_${index}`, // GENERA ID SE NON TROVATO
              name: displayName, // Nome per matching interno
              slug: generatedSlug,
              thumbnail: variation.color || "#808080",
              hover: null, // AGGIUNTO: Default hover a null per materiali generati
              collection: "Default",
            };
          }

          return {
            id: matchingMaterial.id, // PRESERVA L'ID
            name: matchingMaterial.name, // Nome dal database materials.json per il matching interno
            originalName: variation.name, // Store original for reference
            displayName: matchingMaterial.name, // NOME DAL DATABASE materials.json - questo va mostrato!
            slug: matchingMaterial.slug || generatedSlug,
            thumbnail:
              matchingMaterial.thumbnail || variation.color || "#808080",
            hover: matchingMaterial.hover || null, // AGGIUNTO: Passa la proprietà hover dal database
            viewerName: variation.name, // Keep original name for viewer reference
            standard: false, // Will be set by getAvailableMaterials based on family settings
            optional: false, // Will be set by getAvailableMaterials based on family settings
            collection: matchingMaterial.collection || "Default",
          };
        }
      );

      // Add to configurable materials
      this.configurableMaterials.push({
        technicalName: mat.name,
        variations: enrichedVariations,
      });
    });

    this.initialized = true;
    debug.materials(`Initialized ${this.configurableMaterials.length} configurable materials for: ${familyName}`);
    return this.configurableMaterials;
  }

  // Get available materials for configuration with display labels and standard/optional flags
  getAvailableMaterials(
    familyName = null,
    productCode = null,
    language = "it"
  ) {
    if (!this.initialized) {
      console.warn(
        "MaterialManager not initialized before getAvailableMaterials call"
      );
      return [];
    }

    // Use passed parameters or fall back to stored values from initialize
    const actualFamily = familyName || this.currentFamily;
    const actualProduct = productCode || this.currentProduct;

    // Get the correct settings from family or product
    let settings = null;

    // First try product settings (they override family settings)
    if (actualProduct && this.productSettings[actualProduct]) {
      settings = this.productSettings[actualProduct];
    }
    // Then try exact match for family settings
    else if (actualFamily && this.familySettings[actualFamily]) {
      settings = this.familySettings[actualFamily];
    }
    // Try case-insensitive match for family name
    else if (actualFamily) {
      const familyLowerCase = actualFamily.toLowerCase();
      for (const storedFamily in this.familySettings) {
        if (storedFamily.toLowerCase() === familyLowerCase) {
          settings = this.familySettings[storedFamily];

          // Important: Update the reference to use the correct casing
          this.familySettings[actualFamily] = settings;

          // Only delete the old reference if it's different from the new one
          if (storedFamily !== actualFamily) {
            delete this.familySettings[storedFamily];
          }

          break;
        }
      }
    }

    // If still no settings found, use a default empty structure
    if (!settings) {
      console.warn(
        `No settings found for family: ${actualFamily}, product: ${actualProduct}`
      );
      settings = { types: {}, typeLinks: [] };
    }

    // Get translations for this language
    const translations = getTranslations(language);

    // NUOVO: Get collection translations for this family
    const familyCollectionTranslations =
      this.collectionTranslations[actualFamily] || {};

    // Process each configurable material
    const result = this.configurableMaterials.map((material) => {
      const technicalName = material.technicalName;

      // IMPORTANT: Find type settings in a case-insensitive way
      let typeSettings = null;
      for (const typeKey in settings.types) {
        if (typeKey.toLowerCase() === technicalName.toLowerCase()) {
          typeSettings = settings.types[typeKey];
          break;
        }
      }

      // Get translated technical name - Prima prova con le traduzioni della famiglia
      let displayLabel = this.capitalizeFirstLetter(technicalName);
      let technicalTypeTranslation = {};

      if (typeSettings && typeSettings.lang && typeSettings.lang[language]) {
        // Usa la traduzione specifica della famiglia
        displayLabel = typeSettings.lang[language];
        technicalTypeTranslation = { [technicalName]: typeSettings.lang };
      } else if (typeSettings && typeSettings.label) {
        // Fallback alla label generica
        displayLabel = typeSettings.label;
      } else if (
        translations.materialTypes &&
        translations.materialTypes[technicalName]
      ) {
        // Fallback alle traduzioni globali
        displayLabel = translations.materialTypes[technicalName];
      }

      // If no settings for this type, just add display label
      if (!typeSettings) {
        return {
          ...material,
          displayLabel,
          collectionTranslations: familyCollectionTranslations,
          technicalTypeTranslations: technicalTypeTranslation,
        };
      }

      // Process each variation to determine if it's standard or optional
      let variations = material.variations.map((variation) => {
        const name = variation.name || "";
        const slug = variation.slug || this.generateSlug(name);

        // Normalize the variation name and slug
        const normalizedName = this.normalize(name);
        const normalizedSlug = this.normalize(slug);

        // Check if this is a standard material
        let isStandard = false;
        if (Array.isArray(typeSettings.standard)) {
          // Try to match against each standard material
          for (const stdMat of typeSettings.standard) {
            const normalizedStdMat = this.normalize(stdMat);
            if (
              normalizedName === normalizedStdMat ||
              normalizedSlug === normalizedStdMat ||
              name === stdMat ||
              slug === stdMat
            ) {
              isStandard = true;
              break;
            }
          }
        }

        // Check if this is an optional material
        let isOptional = false;
        if (Array.isArray(typeSettings.optional)) {
          // Try to match against each optional material
          for (const optMat of typeSettings.optional) {
            const normalizedOptMat = this.normalize(optMat);
            if (
              normalizedName === normalizedOptMat ||
              normalizedSlug === normalizedOptMat ||
              name === optMat ||
              slug === optMat
            ) {
              isOptional = true;
              break;
            }
          }
        }

        // If it's not standard, mark it as optional
        if (!isStandard) {
          isOptional = true;
        }

        // Translate the collection name - Prima prova con le traduzioni famiglia
        let translatedCollection = variation.collection || "Default";

        // Prima prova con le traduzioni della famiglia
        if (
          familyCollectionTranslations[translatedCollection] &&
          familyCollectionTranslations[translatedCollection][language]
        ) {
          translatedCollection =
            familyCollectionTranslations[translatedCollection][language];
        }
        // Fallback alle traduzioni globali
        else if (
          translations.materialCollections &&
          translations.materialCollections[translatedCollection]
        ) {
          translatedCollection =
            translations.materialCollections[translatedCollection];
        }

        // Return the variation with standard/optional flags and translated collection
        // IMPORTANTE: Mantieni la proprietà hover
        return {
          ...variation,
          standard: isStandard,
          optional: isOptional,
          originalCollection: variation.collection,
          collection: variation.collection,
          hover: variation.hover, // AGGIUNTO: Assicurati che hover sia preservato
        };
      });

      // NEW: Filter materials if display is enabled
      // Controlla se display è true (stringa o booleano)
      const shouldFilter =
        typeSettings.display === true ||
        typeSettings.display === "true" ||
        typeSettings.display === 1 ||
        typeSettings.display === "1";

      if (shouldFilter) {
        debug.materials(`Filtering ${technicalName} (display setting)`);
        const originalCount = variations.length;

        // Prima filtra normalmente
        variations = variations.filter((variation) =>
          this.isVariationInConfiguration(variation, typeSettings)
        );

        // POI deduplica solo i veri duplicati (stesso codice)
        const finalVariations = [];
        const seenCodes = new Map();

        variations.forEach((variation) => {
          const name = variation.name || "";
          const trimmedName = name.trim();

          // Estrai il codice se c'è (es: "V35" da "V35" o "V35 Olive green")
          const codeMatch = trimmedName.match(/^([A-Z]+\d+)/);

          if (codeMatch) {
            const code = codeMatch[1];
            const existing = seenCodes.get(code);

            if (!existing) {
              // Primo materiale con questo codice
              seenCodes.set(code, variation);
              finalVariations.push(variation);
            } else {
              // Duplicato trovato - tieni il più specifico
              const existingName = existing.name || "";
              const currentName = trimmedName;

              // Se quello nuovo ha più dettagli (più lungo + contiene spazio), sostituisci
              if (
                currentName.length > existingName.length &&
                currentName.includes(" ") &&
                !existingName.includes(" ")
              ) {
                // Rimuovi il vecchio e aggiungi il nuovo
                const index = finalVariations.findIndex((v) => v === existing);
                if (index > -1) {
                  finalVariations[index] = variation;
                }
                seenCodes.set(code, variation);
              }
              // Altrimenti ignora il duplicato
            }
          } else {
            // Nessun codice riconosciuto, aggiungilo direttamente
            finalVariations.push(variation);
          }
        });

        variations = finalVariations;
        debug.materials(`Filtered ${technicalName}: ${originalCount} -> ${variations.length}`);
      }

      // Log results
      const standardCount = variations.filter((v) => v.standard).length;
      const optionalCount = variations.filter((v) => v.optional).length;
      debug.materials(`${technicalName}: ${standardCount} std, ${optionalCount} opt ${shouldFilter ? "(filtered)" : ""}`);

      // Return the updated material with variations
      return {
        ...material,
        displayLabel,
        variations,
        collectionTranslations: familyCollectionTranslations,
        technicalTypeTranslations: technicalTypeTranslation,
      };
    });

    return result;
  }

  // Helper method to capitalize first letter
  capitalizeFirstLetter(string) {
    if (!string) return "";
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  // Set family-specific material settings - AGGIORNATO per traduzioni
  setFamilySettings(familyName, settings) {
    if (!familyName) {
      console.error(
        "Cannot set family settings with null or empty family name"
      );
      return;
    }

    // NUOVO: Salva le traduzioni delle collezioni se presenti
    if (settings.collectionTranslations) {
      this.collectionTranslations[familyName] = settings.collectionTranslations;
    }

    // IMPORTANT: Check if we're overwriting with empty/incomplete settings
    const existingSettings = this.familySettings[familyName];
    if (existingSettings) {
      // If we already have settings for this family and the new settings
      // have empty typeLinks or types, use the existing ones
      const newSettings = {
        types: settings.types || {},
        typeLinks: settings.typeLinks || [],
      };

      // Don't override with empty typeLinks
      if (
        newSettings.typeLinks.length === 0 &&
        existingSettings.typeLinks &&
        existingSettings.typeLinks.length > 0
      ) {
        newSettings.typeLinks = existingSettings.typeLinks;
      }

      // Don't override with empty types
      if (
        Object.keys(newSettings.types).length === 0 &&
        existingSettings.types &&
        Object.keys(existingSettings.types).length > 0
      ) {
        newSettings.types = existingSettings.types;
      }

      // Always store with the exact case provided
      this.familySettings[familyName] = newSettings;
    } else {
      // No existing settings, just store the new ones
      this.familySettings[familyName] = {
        types: settings.types || {},
        typeLinks: settings.typeLinks || [],
      };
    }

    // Clean up any old versions of this family that might exist with different casing
    const familyLowerCase = familyName.toLowerCase();
    for (const storedFamily in this.familySettings) {
      if (
        storedFamily !== familyName &&
        storedFamily.toLowerCase() === familyLowerCase
      ) {
        delete this.familySettings[storedFamily];
      }
    }
  }

  // Set product-specific material settings
  setProductSettings(productCode, settings) {
    // Create a structured settings object
    this.productSettings[productCode] = {
      types: settings.types || {},
      typeLinks: settings.typeLinks || [],
    };
  }

  // NUOVO: Funzione per trovare mapping specifico
  findSpecificMapping(familyName, sourceType, targetType, materialName) {
    const familySettings = this.familySettings[familyName];
    if (!familySettings || !familySettings.typeLinks) {
      return null;
    }

    // Cerca nei typeLinks un link che contiene specificMappings
    for (const link of familySettings.typeLinks) {
      if (
        link.specificMappings &&
        link.specificMappings[sourceType] &&
        link.specificMappings[sourceType][targetType]
      ) {
        const mappings = link.specificMappings[sourceType][targetType];

        // Cerca mapping per nome esatto
        if (mappings[materialName]) {
          return mappings[materialName];
        }

        // Cerca mapping normalizzato
        const normalizedName = this.normalize(materialName);
        for (const [key, value] of Object.entries(mappings)) {
          if (this.normalize(key) === normalizedName) {
            return value;
          }
        }
      }
    }

    return null;
  }

  // NUOVA funzione per trovare materiale per slug/codice
  findMaterialBySlug(slug) {
    if (!slug) return null;

    // Pulizia dello slug
    const cleanSlug = typeof slug === "string" ? slug.trim() : slug;

    // Cerca per slug esatto
    const bySlug = this.allMaterials.find(
      (material) => material.slug === cleanSlug || material.id === cleanSlug
    );
    if (bySlug) return bySlug;

    // Cerca per codice (per materiali che hanno codici come V38, ML2, etc.)
    const byCode = this.allMaterials.find((material) => {
      if (!material.name) return false;

      // Estrai il codice dal nome (es. "V38 Signal Black" -> "V38")
      const codeMatch = material.name.match(/^([A-Z]+\d+)\s/);
      if (codeMatch && codeMatch[1] === cleanSlug) {
        return true;
      }

      // Controlla se il nome inizia esattamente con il codice
      return (
        material.name.startsWith(cleanSlug + " ") ||
        material.name.trim() === cleanSlug
      );
    });

    return byCode || null;
  }

  // NUOVA funzione per preservare i mapping codice-nome
  createCodeToNameMapping() {
    const mapping = new Map();

    this.allMaterials.forEach((material) => {
      if (!material.name) return;

      // Per codici tipo "V38", "ML2", etc.
      const codeMatch = material.name.match(/^([A-Z]+\d+)\s*(.*)/);
      if (codeMatch) {
        const code = codeMatch[1];
        mapping.set(code, material.name);
        mapping.set(code.trim(), material.name);

        // Anche il mapping inverso
        mapping.set(material.name, code);
      }

      // Per slug
      if (material.slug) {
        mapping.set(material.slug, material.name);
        mapping.set(material.name, material.slug);
      }
    });

    return mapping;
  }

  // NUOVA funzione per mantenere i codici originali durante l'editing
  preserveOriginalCodes(materials, originalCodes) {
    if (!Array.isArray(materials) || !Array.isArray(originalCodes)) {
      return materials;
    }

    // Se abbiamo lo stesso numero di elementi, usa i codici originali
    if (materials.length === originalCodes.length) {
      return originalCodes;
    }

    // Altrimenti cerca di mappare i nuovi materiali ai codici
    return materials.map((materialName) => {
      // Se sembra già un codice, mantienilo
      if (materialName.match(/^[A-Z]+\d+\s*$/)) {
        return materialName.trim();
      }

      // Cerca il codice corrispondente
      const material = this.findMaterialByName(materialName);
      if (material) {
        const codeMatch = material.name.match(/^([A-Z]+\d+)/);
        if (codeMatch) {
          return codeMatch[1];
        }
        return material.slug || materialName;
      }

      return materialName;
    });
  }

  // Find material by name
  findMaterialByName(name) {
    if (!name) return null;

    const cleanName = typeof name === "string" ? name.trim() : name;

    // Exact match prima
    const exactMatch = this.allMaterials.find(
      (material) => material.name === cleanName
    );
    if (exactMatch) return exactMatch;

    // Se il nome sembra un codice (es. "V38"), cerca per codice
    if (cleanName.match(/^[A-Z]+\d+\s*$/)) {
      const byCode = this.allMaterials.find((material) => {
        if (!material.name) return false;
        return (
          material.name.startsWith(cleanName.trim() + " ") ||
          material.name.trim() === cleanName.trim()
        );
      });
      if (byCode) return byCode;
    }

    // Ricerca case-insensitive
    const caseInsensitive = this.allMaterials.find(
      (material) =>
        material.name && material.name.toLowerCase() === cleanName.toLowerCase()
    );
    if (caseInsensitive) return caseInsensitive;

    // Ricerca parziale
    const partial = this.allMaterials.find(
      (material) =>
        material.name &&
        (material.name.includes(cleanName) || cleanName.includes(material.name))
    );

    return partial || null;
  }

  // Find material by slug
  findMaterialBySlug(slug) {
    if (!slug) return null;

    // Clean the input slug
    slug = String(slug).trim();
    const normalizedSlug = this.normalize(slug);

    // First try exact match
    if (this.materialsBySlug[slug]) {
      return this.materialsBySlug[slug];
    }

    // Then try normalized match
    if (this.materialsBySlug[normalizedSlug]) {
      return this.materialsBySlug[normalizedSlug];
    }

    // Try more complex fuzzy matching for slugs like "ML1 Pure white"
    for (const key in this.materialsBySlug) {
      // Skip non-string keys
      if (typeof key !== "string") continue;

      // If the slug contains our key at the beginning (e.g., "ML1" is at the start of "ML1 Pure white")
      if (slug.toLowerCase().startsWith(key.toLowerCase() + " ")) {
        return this.materialsBySlug[key];
      }

      const normalizedKey = this.normalize(key);
      if (normalizedKey === normalizedSlug) {
        return this.materialsBySlug[key];
      }
    }

    return null;
  }

  // Get materials ordered according to configuration, fallback to alphabetical
  getOrderedMaterials(technicalName, materials, isStandardType = true) {
    if (!materials || materials.length === 0) return [];

    // Prova a ottenere la configurazione per questo tipo tecnico
    let typeSettings = null;

    // Cerca nelle impostazioni della famiglia corrente
    if (this.currentFamily && this.familySettings[this.currentFamily]) {
      const familyTypes = this.familySettings[this.currentFamily].types || {};

      // Cerca con corrispondenza esatta
      typeSettings = familyTypes[technicalName];

      // Se non trovato, prova con case-insensitive
      if (!typeSettings) {
        for (const typeKey in familyTypes) {
          if (typeKey.toLowerCase() === technicalName.toLowerCase()) {
            typeSettings = familyTypes[typeKey];
            break;
          }
        }
      }
    }

    // Se non abbiamo configurazione, usa ordinamento alfabetico
    if (!typeSettings) {
      return materials.sort((a, b) => {
        const aName = a.displayName || a.name || "";
        const bName = b.displayName || b.name || "";
        return aName.localeCompare(bName, undefined, {
          numeric: true,
          sensitivity: "base",
        });
      });
    }

    // Ottieni l'array di ordinamento dalla configurazione
    const orderArray = isStandardType
      ? typeSettings.standard || []
      : typeSettings.optional || [];

    // Se l'array di ordinamento è vuoto, usa ordinamento alfabetico
    if (orderArray.length === 0) {
      return materials.sort((a, b) => {
        const aName = a.displayName || a.name || "";
        const bName = b.displayName || b.name || "";
        return aName.localeCompare(bName, undefined, {
          numeric: true,
          sensitivity: "base",
        });
      });
    }

    // Crea una mappa di indici per l'ordinamento
    const orderMap = new Map();
    orderArray.forEach((item, index) => {
      const cleanItem = String(item).trim();
      orderMap.set(cleanItem, index);

      // Aggiungi anche versioni normalizzate per il matching
      const normalized = this.normalize(cleanItem);
      orderMap.set(normalized, index);
    });

    // Funzione per trovare l'indice di un materiale
    const getOrderIndex = (material) => {
      const name = material.displayName || material.name || "";
      const slug = material.slug || "";
      const cleanName = name.trim();
      const normalizedName = this.normalize(name);
      const normalizedSlug = this.normalize(slug);

      // Prova varie strategie di matching

      // 1. Match esatto per nome
      if (orderMap.has(cleanName)) {
        return orderMap.get(cleanName);
      }

      // 2. Match esatto per slug
      if (orderMap.has(slug)) {
        return orderMap.get(slug);
      }

      // 3. Match normalizzato per nome
      if (orderMap.has(normalizedName)) {
        return orderMap.get(normalizedName);
      }

      // 4. Match normalizzato per slug
      if (orderMap.has(normalizedSlug)) {
        return orderMap.get(normalizedSlug);
      }

      // 5. Prova a matchare con codici (es. "V1" da "V1 Pure white")
      const codeMatch = cleanName.match(/^([A-Z]+\d+)/);
      if (codeMatch) {
        const code = codeMatch[1];
        if (orderMap.has(code)) {
          return orderMap.get(code);
        }
        if (orderMap.has(this.normalize(code))) {
          return orderMap.get(this.normalize(code));
        }
      }

      // 6. Prova match parziale - se il nome del materiale contiene un elemento della configurazione
      for (const [configItem, index] of orderMap.entries()) {
        const cleanConfigItem = String(configItem).trim();

        // Skip se è un valore normalizzato (per evitare duplicati)
        if (
          configItem !== cleanConfigItem &&
          this.normalize(cleanConfigItem) === configItem
        ) {
          continue;
        }

        // Se l'elemento della configurazione è contenuto nel nome del materiale
        if (
          cleanName.toLowerCase().includes(cleanConfigItem.toLowerCase()) ||
          slug.toLowerCase().includes(cleanConfigItem.toLowerCase())
        ) {
          return index;
        }
      }

      // Se non trovato, restituisci un indice alto per metterlo alla fine
      return orderArray.length + 1000;
    };

    // Ordina i materiali usando gli indici
    const sortedMaterials = materials.sort((a, b) => {
      const aIndex = getOrderIndex(a);
      const bIndex = getOrderIndex(b);

      // Se hanno lo stesso indice, usa ordinamento alfabetico
      if (aIndex === bIndex) {
        const aName = a.displayName || a.name || "";
        const bName = b.displayName || b.name || "";
        return aName.localeCompare(bName, undefined, {
          numeric: true,
          sensitivity: "base",
        });
      }

      return aIndex - bIndex;
    });

    return sortedMaterials;
  }

  // Find materials by technical name (for viewer)
  findMaterialsByTechnicalName(technicalName) {
    const materials = this.configurableMaterials.find(
      (m) => m.technicalName === technicalName
    );
    return materials ? materials.variations : [];
  }

  // Get all technical types
  getTechnicalTypes() {
    return this.configurableMaterials.map((m) => m.technicalName);
  }

  // Get materials for a technical type
  getMaterialsForTechnicalType(technicalType) {
    const materials = this.configurableMaterials.find(
      (m) => m.technicalName === technicalType
    );
    return materials ? materials.variations : [];
  }

  // Generate a slug from a name
  generateSlug(name) {
    if (!name) return "";
    return String(name)
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "-") // Replace spaces with hyphens
      .replace(/[^\w\-]+/g, "") // Remove non-word chars except hyphens
      .replace(/\-\-+/g, "-") // Replace multiple hyphens with single hyphen
      .replace(/^-+/, "") // Trim hyphens from start
      .replace(/-+$/, ""); // Trim hyphens from end
  }

  // Convert material name to slug using the database
  nameToSlug(name) {
    const material = this.findMaterialByName(name);
    return material ? material.slug : this.generateSlug(name);
  }

  // Process material type links to apply connected materials
  processMaterialTypeLinks(sourceType, sourceMaterial, selectedMaterials) {
    const updatedMaterials = { ...selectedMaterials };

    // First check product-specific type links
    let productLinks = [];
    const productCode = Object.keys(this.productSettings)[0]; // Example, better to pass this directly

    if (productCode && this.productSettings[productCode]?.typeLinks) {
      productLinks = this.productSettings[productCode].typeLinks;
    }

    // Then check family-specific type links
    let familyLinks = [];
    const familyName = Object.keys(this.familySettings)[0]; // Example, better to pass this directly

    if (familyName && this.familySettings[familyName]?.typeLinks) {
      familyLinks = this.familySettings[familyName].typeLinks;
    }

    // Combine links with product links taking precedence
    const allLinks = [...familyLinks, ...productLinks];

    // Process each link
    allLinks.forEach((link) => {
      // If this link includes our source type
      if (link.linkedTechnicalNames.includes(sourceType)) {
        // For each linked technical type
        link.linkedTechnicalNames.forEach((targetType) => {
          // Skip if it's the source type itself
          if (targetType === sourceType) return;

          // Find materials for this target type
          const targetMaterials = this.getMaterialsForTechnicalType(targetType);

          // First try to match by slug, then by name
          let matchingMaterial = targetMaterials.find(
            (m) => m.slug === sourceMaterial.slug
          );

          if (!matchingMaterial) {
            // If no match by slug, try matching by name
            matchingMaterial = targetMaterials.find(
              (m) => m.name === sourceMaterial.name
            );
          }

          if (!matchingMaterial) {
            // Last resort - try normalized matching
            const normalizedSourceName = this.normalize(sourceMaterial.name);
            const normalizedSourceSlug = this.normalize(sourceMaterial.slug);

            matchingMaterial = targetMaterials.find(
              (m) =>
                this.normalize(m.name) === normalizedSourceName ||
                this.normalize(m.slug) === normalizedSourceSlug
            );
          }

          if (matchingMaterial) {
            // IMPORTANT: Preserve the standard/optional flags from the source material
            updatedMaterials[targetType] = {
              ...matchingMaterial,
              standard: sourceMaterial.standard,
              optional: sourceMaterial.optional,
            };
          }
        });
      }
    });

    return updatedMaterials;
  }
}

// Export singleton
export const materialManager = new MaterialManager();
